import { selectCurrency } from '@/redux/config/configSlice';
import { DiscountType } from '@/redux/data/dataTypes';
import { useAppSelector } from '@/redux/hooks';
import { CloseOutlined } from '@ant-design/icons';
import { Button, Input, Modal } from 'antd';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DiscountCalculatorProps {
  discount: DiscountType;
  onClose: () => void;
  onSubmit: (discount: DiscountType) => void;
}

const DiscountCalculator: FC<DiscountCalculatorProps> = ({ discount, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const currency = useAppSelector(selectCurrency);

  const handleSubmit = () => {
    onSubmit({
      ...discount,
      value: Number(amount),
    });
  };

  const handleChangeAmount = (value: string) => {
    if (discount.type === 'VALUE') {
      const decimal = Number(value.replace(/\D/g, '')) / 100;
      const { format: formatCurrency } = Intl.NumberFormat('en-US', {
        currency: 'USD',
        style: 'currency',
      });
      return setAmount(formatCurrency(decimal || 0).replace('$', ''));
    }
    setAmount(value);
  };

  return (
    <Modal
      open
      onCancel={onClose}
      title={
        <div className="flex items-center gap-2.5">
          <Button type="text" icon={<CloseOutlined className="text-white" />} onClick={onClose} />
          <span className="text-white font-medium max-w-[200px] truncate">{discount.name}</span>
        </div>
      }
      footer={null}
      className="[&_.ant-modal-header]:bg-primary [&_.ant-modal-title]:text-white"
    >
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">{t('common:discount')}</span>
        <div className="flex items-center">
          <span className="text-gray-500 text-lg mr-2">
            {discount.type === 'PERCENT' ? '%' : currency}
          </span>
          <Input
            autoFocus
            value={amount}
            placeholder={discount.type === 'VALUE' ? '0.00' : '0'}
            className="text-lg border-0"
            onChange={(e) => handleChangeAmount(e.target.value)}
          />
        </div>
        <Button
          type="primary"
          className="mt-4"
          disabled={
            discount.type === 'VALUE' ? !(parseFloat(amount) > 0) : !(parseInt(amount) < 100)
          }
          onClick={handleSubmit}
        >
          {t('common:submit')}
        </Button>
      </div>
    </Modal>
  );
};

export default DiscountCalculator;
